import {
  Component, Input, Output, EventEmitter, forwardRef, OnInit
} from '@angular/core';
import {
  ControlValueAccessor, NG_VALUE_ACCESSOR
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  IMultiSelectOption,
  IMultiSelectSettings,
  IMultiSelectTexts
} from 'ngx-bootstrap-multiselect';
import { debounce } from 'lodash-es';

@Component({
  selector: 'oes-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true
    }
  ]
})
export class CustomDropdownComponent implements ControlValueAccessor, OnInit {
  @Input() options: IMultiSelectOption[] = [];
  @Input() mode: 'single' | 'multiple' = 'single';
  @Input() placeholderKey: string = 'dropdown.select';
  @Input() searchable = true;
  @Input() checkAll = false;
  @Input() uncheckAll = false;
  @Input() selectionLimit: number | 'all' = 0;
  @Input() customTexts: Partial<IMultiSelectTexts> = {};
  @Input() showAllSelectedText = true;
  @Input() dynamicTitleMaxItems: number = 3;
  @Input() debounceTime = 400;
  @Input() loading = false;
  /**
   * Enables ngx-bootstrap-multiselect's `isLazyLoad` mode. Required for
   * `searchChanged` to fire on text input — without it the underlying
   * lazyLoad event is never emitted (see the library's filterControl
   * subscription, which gates on `settings.isLazyLoad`).
   *
   * Default `false` preserves the local-filtering behavior of existing
   * consumers (they don't drive options from the server).
   */
  @Input() lazySearch = false;
  /**
   * When true, `searchChanged` also fires when the search box is cleared
   * (empty/undefined filter). Default `false` preserves the historical
   * "only emit when there's a non-empty filter" behavior for existing
   * consumers. Opt in when the consumer needs to drive a default reload
   * on clear (e.g. server-side dropdowns that show "first N on no search").
   */
  @Input() emitEmptyOnSearch = false;

  @Output() searchChanged = new EventEmitter<string>();

  disabled = false;
  innerValue: any;
  translatedTexts: IMultiSelectTexts;
  settings: IMultiSelectSettings;
  value: string[] | string;

  onChange = (_: any) => {};
  onTouched = () => {};

  emitSearch: (event: { filter: string }) => void;
  private rawSearch = (event: { filter: string }) => {
    if (this.emitEmptyOnSearch || event?.filter) {
      this.searchChanged.emit(event?.filter ?? '');
    }
  };

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.translate.get([
      this.placeholderKey,
      'dropdown.checkAll',
      'dropdown.uncheckAll',
      'dropdown.checked',
      'dropdown.checkedPlural',
      'dropdown.searchPlaceholder',
      'dropdown.searchEmptyResult',
      'dropdown.searchNoRenderText',
      'dropdown.allSelected'
    ]).subscribe(translations => {
      const builtIn: IMultiSelectTexts = {
        checkAll: translations['dropdown.checkAll'],
        uncheckAll: translations['dropdown.uncheckAll'],
        checked: translations['dropdown.checked'],
        checkedPlural: translations['dropdown.checkedPlural'],
        searchPlaceholder: translations['dropdown.searchPlaceholder'],
        searchEmptyResult: translations['dropdown.searchEmptyResult'],
        searchNoRenderText: translations['dropdown.searchNoRenderText'],
        defaultTitle: translations[this.placeholderKey],
        allSelected: translations['dropdown.allSelected']
      };

      this.translatedTexts = {
        ...builtIn,
        ...this.customTexts,
        ...(this.showAllSelectedText ? {} : { allSelected: '' })
      };
    });

    this.settings = {
      enableSearch: this.searchable,
      selectionLimit: this.mode === 'multiple'
        ? typeof this.selectionLimit === 'number' ? this.selectionLimit : 0
        : 1,
      closeOnSelect: this.mode === 'single',
      autoUnselect: this.mode === 'single',
      showCheckAll: this.mode === 'multiple' && this.checkAll,
      showUncheckAll: this.mode === 'multiple' && this.uncheckAll,
      checkedStyle: 'fontawesome',
      dynamicTitleMaxItems: this.dynamicTitleMaxItems,
      isLazyLoad: this.lazySearch
    };

    this.emitSearch = debounce(this.rawSearch, Math.max(0, this.debounceTime));
  }

  writeValue(value: any): void {
    const { value: v, innerValue: iv } = this.normalizeInput(value);
    this.value = v;
    this.innerValue = iv;
  }

  /**
   * Propagates a selection to the form — but ONLY when the value actually changed.
   *
   * `ngx-bootstrap-multiselect` emits its model change while initialising and again whenever its
   * `options` input is replaced, not only when a user picks something. Forwarding those echoes
   * calls `onChange()`/`onTouched()` on a control nobody has touched, which marks it dirty AND
   * touched on load. Two things follow, both observed:
   *
   *   1. Every required field the form reveals paints "This is a required field" before the user
   *      has looked at it.
   *   2. On Project -> Information the emission escalates through two nested `ControlValueAccessor`
   *      layers (`specification-form` -> `specification` -> `project-information`), so
   *      `siteFormGroup.dirty` becomes true and the unsaved-changes dialog fires on a page the
   *      user only opened. The native `<select>` this component replaced never did this.
   *
   * Comparing through `normalizeInput` on BOTH sides is what makes the guard work at init: the
   * component starts with `value === undefined` while the first echo arrives as `''` (single) or
   * `[]` (multiple), which are the same selection expressed differently.
   */
  onInternalChange(val: string[]): void {
    const { value: v, innerValue: _ } = this.normalizeInput(val);
    if (this.isSameSelection(v, this.normalizeInput(this.value).value)) {
      return;
    }
    this.value = v;
    this.onChange(v);
    this.onTouched();
  }

  private isSameSelection(next: string | string[], current: string | string[]): boolean {
    if (Array.isArray(next) || Array.isArray(current)) {
      const a = Array.isArray(next) ? next : [next];
      const b = Array.isArray(current) ? current : [current];
      return a.length === b.length && a.every((item, index) => item === b[index]);
    }
    return next === current;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private normalizeInput(value: any): { value: string | string[], innerValue: string[] } {
    if (this.mode === 'single') {
      const str = Array.isArray(value) ? value[0] || '' : value || '';
      return { value: str, innerValue: str ? [str] : [] };
    } else {
      const arr = value || [];
      return { value: arr, innerValue: arr };
    }
  }

  get isPlaceholderActive(): boolean {
    return (
      !this.value ||
      (Array.isArray(this.value) && this.value.length === 0)
    );
  }
}
